import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import * as canvasService from '../services/canvas.service';
import { DEFAULT_SORT_FIELDS, sanitizeSortFields } from '../constants/canvasSort';

// Bookmark view column state key -> fetcher(userId, dateRange)
const BOOKMARK_VIEW_SOURCES = {
    bookmarkedFolders: () => canvasService.fetchBookmarkedFolders(),
    assignedFolders: canvasService.fetchAssignedFolders,
    mentionedMessages: canvasService.fetchMentionedMessages,
};

// Latest request id per bookmark view column, so stale responses are ignored
const bookmarkViewRequestIds = {};

// Bookmark view columns that hold folders (a folder can appear in several)
const BOOKMARK_VIEW_FOLDER_KEYS = ['bookmarkedFolders', 'assignedFolders'];

// Apply `transform` to every loaded bookmark view folder list
const mapBookmarkViewFolders = (state, transform) => Object.fromEntries(
    BOOKMARK_VIEW_FOLDER_KEYS.map((key) => [key, state[key] && transform(state[key])])
);

// Selection mode enum
export const SELECTION_MODE = {
    COPY: 'copy',
    MOVE: 'move'
};

/**
 * Canvas Store - Manages hierarchical item data with full fetch & optimistic updates
 *
 * Strategy:
 * - Navigation (click folder, change filter/sort): Full fetch & replace entire state
 * - User actions (create, update, delete): Optimistic update + server sync
 */
const useCanvasStore = create(
    persist(
        (set, get) => ({
            // ===== Server State (replaced on each fetch) =====
            items: {},              // { depth: items[] } - Items organized by depth level
            selectedPath: [],       // [parentId1, parentId2, ...] - Path from root to current folder
            currentFolder: null,    // { id, name, parent_id } | null
            isLoading: false,
            error: null,

            // ===== Settings (persisted & sent as query params) =====
            archiveStatus: 'excludeArchive',  // "all" | "excludeArchive" | "onlyArchive"
            sortFields: DEFAULT_SORT_FIELDS,  // Array of sort fields in priority order (one per available field)
            sortOrder: 'desc',                // "asc" | "desc"

            // ===== UI State =====
            focusedIndex: null,         // Format: "depth_index" e.g., "2_3"
            currentDepth: 1,            // Currently focused column depth
            previousFocusedIndex: null, // Previous focus index for returning from child column
            selectedItems: [],          // Array of selected items for bulk actions
            selectionMode: null,        // 'copy' | 'move' | null
            selectionAnchor: null,      // { depth, index } - Anchor point for shift-click range selection
            bulkAssignDialogOpen: false,
            keyboardNavigationActive: true, // Whether keyboard navigation is enabled
            buttonHandlers: {},         // { depth: { folder: fn, sheet: fn, doc: fn } }
            openDetailPaneId: null,     // ID of item to show in detail pane

            // ===== Focus Mode State =====
            focusMode: false,           // Whether focus mode is active (double-space toggle)
            focusModeChildrenExpanded: true, // Whether children section is expanded in focus mode
            focusModeMessagesHidden: false, // Whether messages are hidden (only last message + input visible)

            // ===== Clipboard State =====
            clipboard: { items: [], mode: null },  // { items: [], mode: 'copy' | 'move' | null }
            pasteCounter: 0,                       // Incremented after each successful paste to trigger children refresh
            users: [],                             // Users for bulk assign

            // ===== Bookmark View State =====
            // Follows the URL (see Home): /canvas and /folder/:id show the canvas, other Home URLs the bookmark view
            bookmarkViewActive: !/^\/(canvas|folder\/)/.test(window.location.pathname),
            // Bookmark view columns (null = not loaded yet)
            bookmarkedFolders: null,      // Folders bookmarked by the user
            assignedFolders: null,        // Folders assigned to the user
            mentionedMessages: null,      // Channel messages + canvas comments where the user is @mentioned

            // ===== Unread Count State =====
            unreadCounts: {},  // { item_id: { unread_count, own_unread_count } }

            // ===== Session-Only State (not persisted, clears on hard refresh) =====
            recentlyCreatedIds: [],  // IDs of items created this session (newest first) - keeps them at top in creation order

            // ===== Navigation Actions (Full Fetch & Replace) =====

            /**
             * Fetch canvas data for a folder (or root if no folderId)
             * This replaces the entire state with fresh data from server
             */
            fetchCanvas: async (folderId = null) => {
                let { archiveStatus, sortFields, sortOrder, recentlyCreatedIds } = get();
                const sanitizedSortFields = sanitizeSortFields(sortFields);
                if (sanitizedSortFields.join(',') !== sortFields.join(',')) {
                    sortFields = sanitizedSortFields;
                    set({ sortFields: sanitizedSortFields });
                }

                set({ isLoading: true, error: null });

                try {
                    const data = await canvasService.fetchCanvasData({
                        folderId,
                        archiveStatus,
                        sortFields,
                        sortOrder
                    });

                    // Reorder items to keep recently created ones at the top of each depth
                    // This preserves the user's mental model of where they created items
                    const reorderedItems = {};
                    for (const [depth, depthItems] of Object.entries(data.items)) {
                        // Separate recently created items from others
                        const recentlyCreatedSet = new Set(recentlyCreatedIds);
                        const created = depthItems.filter(item => recentlyCreatedSet.has(item.id));
                        const others = depthItems.filter(item => !recentlyCreatedSet.has(item.id));
                        // Sort created items by their creation order (newest first based on array position)
                        created.sort((a, b) => {
                            const indexA = recentlyCreatedIds.indexOf(a.id);
                            const indexB = recentlyCreatedIds.indexOf(b.id);
                            return indexA - indexB; // Lower index = more recent = comes first
                        });
                        // Place recently created items at the beginning, then others in server-sorted order
                        reorderedItems[depth] = [...created, ...others];
                    }

                    // Clear selection on navigation, except when the selection is exactly
                    // the folder being opened (normal click selects + navigates)
                    const { selectedItems: prevSelected, selectionMode: prevMode } = get();
                    const openedFolderId = data.selected_path?.[data.selected_path.length - 1];
                    const keepSelection = prevSelected.length === 1 && prevSelected[0].id === openedFolderId;

                    set({
                        items: reorderedItems,
                        selectedPath: data.selected_path,
                        currentFolder: data.current_folder,
                        isLoading: false,
                        selectedItems: keepSelection ? prevSelected : [],
                        selectionMode: keepSelection ? prevMode : false
                    });

                    return data;
                } catch (error) {
                    console.error('Error fetching canvas:', error);
                    set({
                        isLoading: false,
                        error: error.message || 'Failed to fetch canvas data'
                    });
                    throw error;
                }
            },

            /**
             * Set archive filter and re-fetch
             */
            setArchiveStatus: async (status) => {
                set({ archiveStatus: status });
                Cookies.set('archiveStatus', status);

                const { currentFolder } = get();
                await get().fetchCanvas(currentFolder?.id);
            },

            /**
             * Set sort options and re-fetch
             * @param {string[]} sortFields - Array of sort field names in priority order (all 5 fields)
             *   Valid fields: assignee, star_level, status, created_at, updated_at
             * @param {string} sortOrder - "asc" or "desc"
             */
            setSortFields: async (sortFields, sortOrder = 'desc') => {
                const sanitizedSortFields = sanitizeSortFields(sortFields);
                set({ sortFields: sanitizedSortFields, sortOrder });

                const { currentFolder } = get();
                await get().fetchCanvas(currentFolder?.id);
            },

            // ===== Optimistic Update Actions =====

            /**
             * Create a new folder with optimistic update
             */
            createFolder: async (name, parentId, depth) => {
                const trimmedName = name?.trim();
                if (!trimmedName) {
                    toast.error('Folder name cannot be empty');
                    return;
                }

                const tempId = `temp_${Date.now()}`;
                const optimisticFolder = {
                    id: tempId,
                    name: trimmedName,
                    item_type: 'folder',
                    parent_id: parentId,
                    status: 'TODO',
                    star_level: 0,
                    is_archived: false,
                    is_bookmarked: false,
                    employees: [],
                    tags: [],
                    children_count: 0,
                    _isPending: true
                };

                // Optimistic: add to items immediately at the beginning
                set(state => ({
                    items: {
                        ...state.items,
                        [depth]: [optimisticFolder, ...(state.items[depth] || [])]
                    }
                }));

                try {
                    // Server call
                    const serverFolder = await canvasService.createFolder(trimmedName, parentId);

                    // Replace temp with real data and track the new ID
                    set(state => ({
                        items: {
                            ...state.items,
                            [depth]: state.items[depth].map(f =>
                                f.id === tempId ? { ...serverFolder, children_count: 0 } : f
                            )
                        },
                        // Track the newly created folder ID to preserve its position (newest first)
                        recentlyCreatedIds: [serverFolder.id, ...state.recentlyCreatedIds]
                    }));

                    toast.success('Folder created successfully');
                    return serverFolder;
                } catch (error) {
                    // Rollback: remove optimistic folder
                    set(state => ({
                        items: {
                            ...state.items,
                            [depth]: state.items[depth].filter(f => f.id !== tempId)
                        }
                    }));

                    toast.error(error.message || 'Failed to create folder');
                    throw error;
                }
            },

            /**
             * Create a new link with optimistic update
             */
            createLink: async (link, name, parentId, depth) => {
                const tempId = `temp_${Date.now()}`;
                const optimisticLink = {
                    id: tempId,
                    name: name || link,
                    link,
                    item_type: 'link',
                    parent_id: parentId,
                    star_level: 0,
                    is_archived: false,
                    is_bookmarked: false,
                    employees: [],
                    tags: [],
                    _isPending: true
                };

                // Optimistic: add to items immediately
                set(state => ({
                    items: {
                        ...state.items,
                        [depth]: [optimisticLink, ...(state.items[depth] || [])]
                    }
                }));

                try {
                    const serverLink = await canvasService.createLink(link, name, parentId);

                    // Replace temp with real data and track the new ID
                    set(state => ({
                        items: {
                            ...state.items,
                            [depth]: state.items[depth].map(l =>
                                l.id === tempId ? serverLink : l
                            )
                        },
                        // Track the newly created link ID to preserve its position (newest first)
                        recentlyCreatedIds: [serverLink.id, ...state.recentlyCreatedIds]
                    }));

                    toast.success('Link saved successfully');
                    return serverLink;
                } catch (error) {
                    // Rollback
                    set(state => ({
                        items: {
                            ...state.items,
                            [depth]: state.items[depth].filter(l => l.id !== tempId)
                        }
                    }));

                    toast.error(error.message || 'Failed to create link');
                    throw error;
                }
            },

            /**
             * Update an item optimistically (for star level, archive status, etc.)
             */
            updateItemOptimistic: (itemId, depth, updates) => {
                set(state => ({
                    items: {
                        ...state.items,
                        [depth]: (state.items[depth] || []).map(item =>
                            item.id === itemId ? { ...item, ...updates } : item
                        )
                    }
                }));
            },

            /**
             * Toggle star level with optimistic update
             */
            toggleStarLevel: async (item, depth) => {
                const prevStarLevel = item.star_level;
                const newStarLevel = (prevStarLevel + 1) % 4; // Cycle 0->1->2->3->0

                // Optimistic update
                get().updateItemOptimistic(item.id, depth, { star_level: newStarLevel });

                try {
                    const result = await canvasService.toggleStarLevel(item.id);

                    // Update with server value (might differ)
                    get().updateItemOptimistic(item.id, depth, { star_level: result.star_level });

                    const levelNames = { 0: "Unstarred", 1: "Level 1", 2: "Level 2", 3: "Level 3" };
                    toast.success(`Star level: ${levelNames[result.star_level]}`);

                    return result.star_level;
                } catch (error) {
                    // Rollback
                    get().updateItemOptimistic(item.id, depth, { star_level: prevStarLevel });
                    toast.error('Error updating star level');
                    throw error;
                }
            },

            /**
             * Toggle archive status with optimistic update
             */
            toggleArchiveStatus: async (item, depth) => {
                const prevArchived = item.is_archived;
                const newArchived = !prevArchived;

                // Optimistic update
                get().updateItemOptimistic(item.id, depth, { is_archived: newArchived });

                try {
                    const result = await canvasService.updateItemArchiveStatus(item.id, newArchived);

                    toast.success(newArchived ? 'Item archived' : 'Item unarchived');

                    // If filtering by archive status, the item might need to be removed
                    const { archiveStatus } = get();
                    if (archiveStatus !== 'all') {
                        // Re-fetch to ensure consistency
                        const { currentFolder } = get();
                        await get().fetchCanvas(currentFolder?.id);
                    }

                    return result;
                } catch (error) {
                    // Rollback
                    get().updateItemOptimistic(item.id, depth, { is_archived: prevArchived });
                    toast.error('Error updating archive status');
                    throw error;
                }
            },

            /**
             * Delete an item with optimistic update
             */
            deleteItem: async (item, depth) => {
                // Store for rollback
                const prevItems = get().items[depth];

                // Optimistic: remove from list
                set(state => ({
                    items: {
                        ...state.items,
                        [depth]: state.items[depth].filter(i => i.id !== item.id)
                    }
                }));

                try {
                    if (item.item_type === 'folder') {
                        await canvasService.deleteFolder(item.id);
                    } else {
                        await canvasService.deleteLink(item.id);
                    }

                    toast.success(`${item.item_type === 'folder' ? 'Folder' : 'Link'} deleted`);
                } catch (error) {
                    // Rollback
                    set(state => ({
                        items: {
                            ...state.items,
                            [depth]: prevItems
                        }
                    }));

                    toast.error('Error deleting item');
                    throw error;
                }
            },

            // ===== UI State Actions =====

            setFocusedIndex: (index) => set({ focusedIndex: index }),

            setCurrentDepth: (depth) => set({ currentDepth: depth }),

            setPreviousFocusedIndex: (index) => set({ previousFocusedIndex: index }),

            setKeyboardNavigationActive: (active) => set({ keyboardNavigationActive: active }),

            setButtonHandlers: (depth, handlers) => set(state => ({
                buttonHandlers: { ...state.buttonHandlers, [depth]: handlers }
            })),

            setOpenDetailPaneId: (id) => set({ openDetailPaneId: id }),

            // ===== Focus Mode Actions =====
            toggleFocusMode: () => set(state => ({
                focusMode: !state.focusMode
            })),

            setFocusMode: (active) => set({ focusMode: active }),

            setFocusModeChildrenExpanded: (expanded) => set({ focusModeChildrenExpanded: expanded }),
            setFocusModeMessagesHidden: (hidden) => set({ focusModeMessagesHidden: hidden }),

            // ===== Bookmark View Actions =====

            setBookmarkViewActive: (active) => set({ bookmarkViewActive: active }),

            /**
             * Fetch one bookmark view column. Clears it to null (loading) first;
             * only the latest request for a column is applied.
             * @param {string} key - Column state key (see BOOKMARK_VIEW_SOURCES)
             * @param {number} userId - Current user ID
             * @param {{dateFrom, dateTo}} dateRange - Optional date range
             */
            fetchBookmarkViewColumn: async (key, userId, dateRange) => {
                const requestId = (bookmarkViewRequestIds[key] || 0) + 1;
                bookmarkViewRequestIds[key] = requestId;
                set({ [key]: null });

                let items;
                try {
                    items = await BOOKMARK_VIEW_SOURCES[key](userId, dateRange);
                } catch (error) {
                    console.error(`Error fetching ${key}:`, error);
                    items = [];
                }
                if (bookmarkViewRequestIds[key] === requestId) {
                    set({ [key]: items });
                }
            },

            /**
             * Patch a folder in every bookmark view folder column (after an edit in the details modal)
             */
            updateBookmarkViewFolder: (id, updates) => set(state => mapBookmarkViewFolders(state,
                folders => folders.map(folder => folder.id === id ? { ...folder, ...updates } : folder)
            )),

            /**
             * Remove a folder from every bookmark view folder column (after it is deleted)
             */
            removeBookmarkViewFolder: (id) => set(state => mapBookmarkViewFolders(state,
                folders => folders.filter(folder => folder.id !== id)
            )),

            // ===== Unread Count Actions =====

            /**
             * Fetch unread counts for direct children of a folder
             * @param {number|null} folderId - Folder ID (null for root)
             */
            /**
             * Fetch unread counts for children of a folder
             * @param {number|null} folderId - Parent folder ID (null for root)
             * @param {Array<number>|null} filterIds - If provided, only store counts for these item IDs
             */
            fetchUnreadCounts: async (folderId = null, filterIds = null) => {
                try {
                    const counts = await canvasService.fetchUnreadCounts(folderId);

                    // If filterIds provided, only keep counts for those specific items
                    let filteredCounts = counts;
                    if (filterIds && filterIds.length > 0) {
                        filteredCounts = {};
                        filterIds.forEach(id => {
                            if (counts[id] !== undefined) {
                                filteredCounts[id] = counts[id];
                            }
                        });
                    }

                    set(state => ({
                        unreadCounts: { ...state.unreadCounts, ...filteredCounts }
                    }));
                    return filteredCounts;
                } catch (error) {
                    console.error('Error fetching unread counts:', error);
                    return {};
                }
            },

            /**
             * Mark all comments as read for an item
             * Uses optimistic update for immediate UI feedback with ancestor propagation
             * Refetches all ancestor counts to ensure consistency with server
             * @param {number} itemId - Item ID to mark as read
             */
            markAllRead: async (itemId) => {
                const { currentFolder, selectedPath, unreadCounts } = get();

                // Get the own_unread_count to clear (this is how much to decrement from ancestors)
                const ownUnreadToClear = unreadCounts[itemId]?.own_unread_count || 0;

                // Optimistic update: clear own_unread_count and propagate to ancestors
                if (ownUnreadToClear > 0) {
                    set(state => {
                        const newCounts = { ...state.unreadCounts };

                        // Clear own_unread_count for the item itself
                        if (newCounts[itemId]) {
                            const currentOwnUnread = newCounts[itemId].own_unread_count || 0;
                            newCounts[itemId] = {
                                ...newCounts[itemId],
                                own_unread_count: 0,
                                // Also decrement the aggregate unread_count
                                unread_count: Math.max(0, (newCounts[itemId].unread_count || 0) - currentOwnUnread)
                            };
                        }

                        // Propagate the reduction to all ancestors in the path
                        if (selectedPath && selectedPath.length > 0) {
                            selectedPath.forEach(ancestorId => {
                                if (newCounts[ancestorId]) {
                                    newCounts[ancestorId] = {
                                        ...newCounts[ancestorId],
                                        unread_count: Math.max(0, (newCounts[ancestorId].unread_count || 0) - ownUnreadToClear)
                                    };
                                }
                            });
                        }

                        return { unreadCounts: newCounts };
                    });
                }

                try {
                    // Server sync
                    await canvasService.markAllRead(itemId);

                    // Refetch unread counts for visible levels to ensure consistency
                    const fetchPromises = [];

                    // Fetch current folder's children (or root if at root level)
                    const targetFolderId = currentFolder?.id || null;
                    fetchPromises.push(get().fetchUnreadCounts(targetFolderId));

                    // If we're inside a folder, fetch for ancestors in the path
                    if (selectedPath && selectedPath.length > 0) {
                        // Fetch root counts but ONLY keep the count for the root ancestor in path
                        const rootAncestorId = selectedPath[0];
                        fetchPromises.push(get().fetchUnreadCounts(null, [rootAncestorId]));

                        // Fetch counts for each ancestor's children
                        const uniqueAncestors = [...new Set(selectedPath)];
                        uniqueAncestors.forEach(ancestorId => {
                            if (ancestorId !== targetFolderId) {
                                fetchPromises.push(get().fetchUnreadCounts(ancestorId));
                            }
                        });
                    }

                    // Wait for all refetches to complete
                    await Promise.all(fetchPromises);
                } catch (error) {
                    console.error('Error marking all read:', error);
                }
            },

            toggleItemSelection: (item, depth, itemIndex) => {
                set(state => {
                    const isSelected = state.selectedItems.some(i => i.id === item.id);

                    if (isSelected) {
                        const newSelectedItems = state.selectedItems.filter(i => i.id !== item.id);
                        return {
                            selectedItems: newSelectedItems,
                            selectionMode: newSelectedItems.length > 0,
                            // Clear anchor if no items selected
                            selectionAnchor: newSelectedItems.length > 0 ? state.selectionAnchor : null
                        };
                    } else {
                        return {
                            selectedItems: [...state.selectedItems, { ...item, depth }],
                            selectionMode: true,
                            // Set anchor to this item for range selection
                            selectionAnchor: { depth, index: itemIndex }
                        };
                    }
                });
            },

            /**
             * Replace the current selection with a single item
             * Used for a normal (unmodified) click
             */
            selectSingleItem: (item, depth, itemIndex) => {
                set({
                    selectedItems: [{ ...item, depth }],
                    selectionMode: true,
                    selectionAnchor: { depth, index: itemIndex }
                });
            },

            /**
             * Select a range of items from the anchor to the clicked item
             * Used for Shift+Click range selection
             */
            selectItemRange: (clickedItem, clickedDepth, clickedIndex) => {
                const { selectionAnchor, items, selectedItems } = get();

                // If no anchor or different depth, just select the clicked item
                if (!selectionAnchor || selectionAnchor.depth !== clickedDepth) {
                    set({
                        selectedItems: [{ ...clickedItem, depth: clickedDepth }],
                        selectionMode: true,
                        selectionAnchor: { depth: clickedDepth, index: clickedIndex }
                    });
                    return;
                }

                const itemsAtDepth = items[clickedDepth] || [];
                const anchorIndex = selectionAnchor.index;

                // Determine range bounds
                const startIndex = Math.min(anchorIndex, clickedIndex);
                const endIndex = Math.max(anchorIndex, clickedIndex);

                // Get all items in the range
                const rangeItems = itemsAtDepth.slice(startIndex, endIndex + 1).map(item => ({
                    id: item.id,
                    parent_id: item.parent_id || item.parent,
                    depth: clickedDepth,
                    item_type: item.item_type,
                    name: item.name,
                    is_archived: item.is_archived || false,
                    employees: item.employees || (item.assignee ? [item.assignee] : []),
                    original_item: item.original_item // For shortcuts
                }));

                // Merge with existing selections from other depths
                const otherDepthSelections = selectedItems.filter(item => item.depth !== clickedDepth);

                set({
                    selectedItems: [...otherDepthSelections, ...rangeItems],
                    selectionMode: true
                    // Keep the anchor unchanged for subsequent range selections
                });
            },

            /**
             * Set selection anchor without selecting an item
             * Used when Ctrl+Click sets a starting point
             */
            setSelectionAnchor: (depth, index) => {
                set({ selectionAnchor: { depth, index } });
            },

            clearSelection: () => set({ selectedItems: [], selectionMode: null, selectionAnchor: null }),

            setBulkAssignDialogOpen: (open) => set({ bulkAssignDialogOpen: open }),

            // ===== Clipboard Actions =====

            setClipboard: (items, mode) => {
                set({ clipboard: { items, mode } });
                localStorage.setItem('clipboard_items', JSON.stringify(items));
                localStorage.setItem('clipboard_mode', mode);
            },

            clearClipboard: () => {
                set({ clipboard: { items: [], mode: null } });
                localStorage.removeItem('clipboard_items');
                localStorage.removeItem('clipboard_mode');
            },

            /**
             * Copy items (Ctrl+C) - for creating shortcuts
             */
            copyItems: (folderId = null) => {
                const { selectedItems, items, focusedIndex, currentDepth } = get();

                if (selectedItems.length > 0) {
                    get().setClipboard(selectedItems, SELECTION_MODE.COPY);
                    set({ selectionMode: SELECTION_MODE.COPY });
                    toast.success(`${selectedItems.length} item(s) ready to create shortcuts`);
                } else if (folderId) {
                    // Find item by folderId
                    const currentItem = get().findItemById(parseInt(folderId));
                    if (currentItem) {
                        get().setClipboard([currentItem], SELECTION_MODE.COPY);
                        set({ selectedItems: [currentItem], selectionMode: SELECTION_MODE.COPY });
                        toast.success("Item selected for creating shortcut");
                    }
                } else {
                    toast("No item selected");
                }
            },

            /**
             * Cut items (Ctrl+X) - for moving
             */
            cutItems: (folderId = null) => {
                const { selectedItems } = get();

                if (selectedItems.length > 0) {
                    get().setClipboard(selectedItems, SELECTION_MODE.MOVE);
                    set({ selectionMode: SELECTION_MODE.MOVE });
                    toast.success(`${selectedItems.length} item(s) ready to move`);
                } else if (folderId) {
                    const currentItem = get().findItemById(parseInt(folderId));
                    if (currentItem) {
                        get().setClipboard([currentItem], SELECTION_MODE.MOVE);
                        set({ selectedItems: [currentItem], selectionMode: SELECTION_MODE.MOVE });
                        toast.success("Item selected for moving");
                    }
                } else {
                    toast("No item selected");
                }
            },

            /**
             * Paste items (Ctrl+V) - execute copy/move operation
             */
            pasteItems: async (targetFolderId) => {
                const { selectedItems, selectionMode, selectedPath, clipboard } = get();
                const now = Date.now();

                // Check for channel messages in localStorage first
                const copiedMessagesStr = localStorage.getItem('copiedChannelMessages');
                if (copiedMessagesStr) {
                    try {
                        const copiedData = JSON.parse(copiedMessagesStr);
                        if (now - copiedData.timestamp <= 5 * 60 * 1000) {
                            await get().pasteChannelMessages(copiedData.messages, targetFolderId);
                            return;
                        } else {
                            localStorage.removeItem('copiedChannelMessages');
                        }
                    } catch (e) {
                        console.error("Failed to parse channel messages", e);
                        localStorage.removeItem('copiedChannelMessages');
                    }
                }

                // Check for single channel message
                const copiedMessageStr = localStorage.getItem('copiedChannelMessage');
                if (copiedMessageStr) {
                    try {
                        const copiedMessage = JSON.parse(copiedMessageStr);
                        if (now - copiedMessage.timestamp <= 5 * 60 * 1000) {
                            await get().pasteChannelMessage(copiedMessage, targetFolderId);
                            return;
                        } else {
                            localStorage.removeItem('copiedChannelMessage');
                        }
                    } catch (e) {
                        console.error("Failed to parse channel message", e);
                        localStorage.removeItem('copiedChannelMessage');
                    }
                }

                // Get items from clipboard (state or localStorage)
                let itemsToPaste = clipboard.items.length > 0 ? clipboard.items : selectedItems;
                let pasteMode = clipboard.mode || selectionMode;

                if (itemsToPaste.length === 0) {
                    const storedItems = localStorage.getItem('clipboard_items');
                    const storedMode = localStorage.getItem('clipboard_mode');

                    if (storedItems && storedMode) {
                        try {
                            itemsToPaste = JSON.parse(storedItems);
                            pasteMode = storedMode;
                        } catch (e) {
                            console.error("Failed to parse clipboard data", e);
                        }
                    }
                }

                if (itemsToPaste.length === 0) {
                    toast.error("No items selected. Use Ctrl+C or Ctrl+X first.");
                    return;
                }

                if (!pasteMode) {
                    toast.error("No operation selected. Use Ctrl+C for shortcuts or Ctrl+X for moving.");
                    return;
                }

                // Validate all items have same parent
                const parents = [...new Set(itemsToPaste.map(item => item.parent_id || item.parent))];
                if (parents.length > 1) {
                    toast.error("Selected items must be from the same parent");
                    get().clearSelection();
                    get().clearClipboard();
                    return;
                }

                try {
                    let response;
                    if (pasteMode === SELECTION_MODE.MOVE) {
                        response = await canvasService.batchMoveItems(
                            itemsToPaste.map(item => item.id),
                            targetFolderId
                        );
                    } else if (pasteMode === SELECTION_MODE.COPY) {
                        const originalItemIds = itemsToPaste.map(item =>
                            item.item_type === 'shortcut' ? item.original_item : item.id
                        );
                        response = await canvasService.batchCreateShortcuts(originalItemIds, targetFolderId);
                    }

                    const { success_count, failure_count, message } = response;

                    if (success_count > 0 && failure_count === 0) {
                        toast.success(message || `${success_count} item(s) processed successfully`);
                        get().clearSelection();
                        get().clearClipboard();
                        // Re-fetch canvas to show updated data
                        await get().fetchCanvas(targetFolderId);
                        // Signal to FocusModeView to re-fetch children
                        set(state => ({ pasteCounter: state.pasteCounter + 1 }));
                    }
                } catch (error) {
                    get().handlePasteError(error);
                }
            },

            /**
             * Paste channel messages as folders with comments
             */
            pasteChannelMessages: async (messages, targetFolderId) => {
                toast.loading(`Creating ${messages.length} items...`);

                try {
                    const foldersToCreate = messages.map(msg => ({
                        name: get().generateFolderTitleFromMessage(msg)
                    }));

                    const bulkResponse = await canvasService.bulkCreateFolders(foldersToCreate, targetFolderId);
                    const createdFolders = bulkResponse.folders || [];

                    // Add comments in parallel
                    const commentPromises = createdFolders.map(async (folder, i) => {
                        const message = messages[i];
                        try {
                            const commentFormData = new FormData();
                            const commentBody = message.message_text || `Pasted from channel by ${message.author}`;
                            commentFormData.append('comment', commentBody);

                            if (message.media_url) {
                                commentFormData.append('media_type', message.media_type || 'other');
                                commentFormData.append('media_url', message.media_url);
                            } else if (message.message_text) {
                                const blob = new Blob([message.message_text], { type: 'text/plain' });
                                const safeTitle = (folder.name || 'transcript').replace(/[^a-z0-9._-]/gi, '_').slice(0, 60);
                                commentFormData.append('media_file', blob, `${safeTitle}_transcript.txt`);
                                commentFormData.append('media_type', 'other');
                            }

                            if (message.channel_message_id) {
                                commentFormData.append('channel_message_id', message.channel_message_id);
                            }

                            await canvasService.addItemComment(folder.id, commentFormData);
                            return { success: true };
                        } catch (error) {
                            console.error('Failed to add comment:', error);
                            return { success: false };
                        }
                    });

                    await Promise.all(commentPromises);
                    toast.dismiss();
                    toast.success(`Created ${createdFolders.length} items from channel messages`);

                    // Re-fetch canvas
                    await get().fetchCanvas(targetFolderId);
                } catch (error) {
                    console.error('Failed to create folders:', error);
                    toast.dismiss();
                    toast.error('Failed to create items');
                }

                localStorage.removeItem('copiedChannelMessages');
            },

            /**
             * Paste single channel message
             */
            pasteChannelMessage: async (copiedMessage, targetFolderId) => {
                const itemTitle = get().generateFolderTitleFromMessage(copiedMessage);

                try {
                    const newFolder = await canvasService.createFolder(itemTitle, targetFolderId);

                    const commentFormData = new FormData();
                    const commentBody = copiedMessage.message_text || `Pasted from channel by ${copiedMessage.author}`;
                    commentFormData.append('comment', commentBody);

                    if (copiedMessage.media_url) {
                        commentFormData.append('media_type', copiedMessage.media_type || 'other');
                        commentFormData.append('media_url', copiedMessage.media_url);
                    } else if (copiedMessage.message_text) {
                        const blob = new Blob([copiedMessage.message_text], { type: 'text/plain' });
                        const safeTitle = (itemTitle || 'transcript').replace(/[^a-z0-9._-]/gi, '_').slice(0, 60);
                        commentFormData.append('media_file', blob, `${safeTitle}_transcript.txt`);
                        commentFormData.append('media_type', 'other');
                    }

                    if (copiedMessage.channel_message_id) {
                        commentFormData.append('channel_message_id', copiedMessage.channel_message_id);
                    }

                    await canvasService.addItemComment(newFolder.id, commentFormData);
                    toast.success(`Created item: ${itemTitle}`);

                    // Re-fetch canvas
                    await get().fetchCanvas(targetFolderId);
                } catch (error) {
                    console.error('Failed to create item:', error);
                    toast.error('Failed to create item');
                }

                localStorage.removeItem('copiedChannelMessage');
            },

            /**
             * Generate folder title from channel message
             */
            generateFolderTitleFromMessage: (message) => {
                let itemTitle = message.message_text || '';
                if (!itemTitle && message.media_type === 'audio') {
                    const urlParts = message.media_url.split('/');
                    const filename = urlParts[urlParts.length - 1];
                    const cleanFilename = filename.split(':::')[1] || filename;
                    itemTitle = `Audio Recording - ${cleanFilename.replace('.wav', '')}`;
                } else if (!itemTitle) {
                    itemTitle = `${message.media_type || 'Media'} from channel - ${new Date(message.created_at).toLocaleString()}`;
                }
                if (itemTitle.length > 100) {
                    itemTitle = itemTitle.substring(0, 97) + '...';
                }
                return itemTitle;
            },

            /**
             * Handle paste errors
             */
            handlePasteError: (error) => {
                if (error.response) {
                    const { status, data } = error.response;
                    if (status === 400 && data.errors?.length > 0) {
                        data.errors.slice(0, 3).forEach(err => toast.error(err));
                        if (data.errors.length > 3) {
                            toast.error(`...and ${data.errors.length - 3} more error(s)`);
                        }
                    } else if (data.error) {
                        toast.error(data.error);
                    } else {
                        toast.error("Operation failed. Please try again.");
                    }
                } else {
                    toast.error("Network error. Please check your connection.");
                    console.error(error);
                }
            },

            // ===== Users Actions =====

            setUsers: (users) => set({ users }),

            fetchUsers: async () => {
                try {
                    const data = await canvasService.fetchUsersAndTags();
                    set({ users: data.users || [] });
                    return data.users || [];
                } catch (error) {
                    console.error('Error fetching users:', error);
                    return [];
                }
            },

            // ===== Bulk Assign Actions =====

            /**
             * Bulk assign items to assignees
             */
            bulkAssign: async (assignees) => {
                const { selectedItems, currentFolder } = get();

                if (!assignees || assignees.length === 0) {
                    toast.error("Please select at least one assignee");
                    return;
                }

                if (selectedItems.length === 0) {
                    toast.error("No items selected");
                    return;
                }

                const itemIds = selectedItems.map(item => item.id);
                let totalSuccess = 0;
                let totalErrors = 0;
                let totalNotified = 0;

                try {
                    for (const assignee of assignees) {
                        try {
                            const response = await canvasService.bulkAssignItems(itemIds, assignee.id);
                            totalSuccess++;
                            totalNotified += response.notifications_queued || 0;
                        } catch (error) {
                            console.error(`Error assigning to ${assignee.name}:`, error);
                            totalErrors++;
                        }
                    }

                    // Show result
                    if (totalSuccess > 0) {
                        const assigneeNames = assignees.slice(0, 3).map(a => a.name || a.username).join(', ');
                        const moreText = assignees.length > 3 ? ` and ${assignees.length - 3} more` : '';
                        const notifyStatus = totalNotified > 0 ? ` (notifying ${totalNotified})` : '';
                        toast.success(`${selectedItems.length} item(s) assigned to ${assigneeNames}${moreText}${notifyStatus}`);
                    }
                    if (totalErrors > 0) {
                        toast.error(`Failed to assign to ${totalErrors} user(s)`);
                    }

                    get().clearSelection();
                    get().setBulkAssignDialogOpen(false);

                    // Re-fetch to show updated data
                    await get().fetchCanvas(currentFolder?.id);
                } catch (error) {
                    toast.error("Failed to bulk assign items");
                    console.error(error);
                }
            },

            /**
             * Bulk remove assignments from items
             */
            bulkRemoveAssignees: async (assignees) => {
                const { selectedItems, currentFolder } = get();

                if (!assignees || assignees.length === 0) return;

                // Check for shortcuts
                const shortcuts = selectedItems.filter(item =>
                    item.item_type === 'shortcut' || item.type === 'shortcut'
                );
                if (shortcuts.length > 0) {
                    toast.error("Removing assignees not supported for shortcuts.");
                    return;
                }

                let totalSuccess = 0;
                let totalErrors = 0;

                try {
                    for (const item of selectedItems) {
                        for (const assignee of assignees) {
                            try {
                                if (item.item_type === 'folder' || item.type === 'folder') {
                                    await canvasService.updateFolderAssignee(item.id, assignee.id, true);
                                } else {
                                    await canvasService.updateLinkAssignee(item.id, assignee.id, true);
                                }
                                totalSuccess++;
                            } catch (error) {
                                console.error(`Error removing assignee:`, error);
                                totalErrors++;
                            }
                        }
                    }

                    if (totalSuccess > 0) {
                        const assigneeNames = assignees.slice(0, 3).map(a => a.name || a.username).join(', ');
                        const moreText = assignees.length > 3 ? ` and ${assignees.length - 3} more` : '';
                        toast.success(`Removed ${assigneeNames}${moreText} from ${selectedItems.length} item(s)`);
                    }
                    if (totalErrors > 0) {
                        toast.error(`Failed to remove ${totalErrors} assignment(s)`);
                    }

                    get().clearSelection();
                    get().setBulkAssignDialogOpen(false);

                    // Re-fetch to show updated data
                    await get().fetchCanvas(currentFolder?.id);
                } catch (error) {
                    toast.error("Failed to remove assignments");
                    console.error(error);
                }
            },

            // ===== Helper Getters =====

            /**
             * Get items at a specific depth
             */
            getItemsAtDepth: (depth) => {
                return get().items[depth] || [];
            },

            /**
             * Get the maximum depth currently loaded
             */
            getMaxDepth: () => {
                const { items } = get();
                const depths = Object.keys(items).map(Number);
                return depths.length > 0 ? Math.max(...depths) : 0;
            },

            /**
             * Check if an item is selected
             */
            isItemSelected: (itemId) => {
                return get().selectedItems.some(item => item.id === itemId);
            },

            /**
             * Find item by ID across all depths
             */
            findItemById: (itemId) => {
                const { items } = get();
                for (const depth in items) {
                    const found = items[depth].find(item => item.id === itemId);
                    if (found) {
                        return {
                            ...found,
                            depth: parseInt(depth)
                        };
                    }
                }
                return null;
            },

            // ===== Reset =====
            reset: () => set({
                items: {},
                selectedPath: [],
                currentFolder: null,
                isLoading: false,
                error: null,
                focusedIndex: null,
                currentDepth: 1,
                previousFocusedIndex: null,
                selectedItems: [],
                selectionMode: null,
                selectionAnchor: null,
                keyboardNavigationActive: true,
                buttonHandlers: {},
                openDetailPaneId: null,
                clipboard: { items: [], mode: null },
                users: [],
                focusMode: false,
                focusModeChildrenExpanded: true,
                focusModeMessagesHidden: false,
                bookmarkViewActive: true,
                bookmarkedFolders: null,
                assignedFolders: null,
                mentionedMessages: null,
                unreadCounts: {},
                recentlyCreatedIds: []
            })
        }),
        {
            name: 'canvas-store',
            // Only persist settings, not data
            partialize: (state) => ({
                archiveStatus: state.archiveStatus,
                sortFields: state.sortFields,
                sortOrder: state.sortOrder,
                focusMode: state.focusMode,
                focusModeChildrenExpanded: state.focusModeChildrenExpanded,
                focusModeMessagesHidden: state.focusModeMessagesHidden,
                currentFolder: state.currentFolder
            })
        }
    )
);

export default useCanvasStore;
